import { useContext } from 'react';
import { TableBody, Typography, Link, TableContainer, Tooltip, Box, Icon } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { ConfigContext } from '@chr/navisphereonline-shared-components';

import { Section } from '@/common/components/section/Section';
import { EmptyContent } from '@/common/components/empty-content/EmptyContent';
import { StyledTable, StyledTableCell, StyledTableHead, StyledTableRow } from '@/common/components/table';
import { TransportMode } from '@/common/constants/booking-types';

export declare interface TransportationPlansProps {
  shipmentNumber?: string;
  transportMode?: TransportMode;
  billOfLadingNumber?: string;
  plannedDate?: string;
  canAccessShipment?: boolean;
  isLinkedToGfsShipment?: boolean;
}

export const TransportationPlan = (
  {
    shipmentNumber,
    transportMode,
    billOfLadingNumber,
    plannedDate,
    canAccessShipment,
    isLinkedToGfsShipment
  }: TransportationPlansProps) => {

  const translate = useTranslation();
  const config = useContext(ConfigContext) as AppConfig;
  const shipmentDetailsBaseUrl = `${config.baseUrl}/shipments/#/detail/`;
  const hasShipment = !!shipmentNumber;

  const InvalidTransportationPlan = () => (
    <EmptyContent
      title={translate('Shipment Unavailable')}
      bodyText={translate('The shipment linked to this booking cannot be displayed.')}
      hideBorder={true}
      illustration={'none'}
    />
  );

  const EmptyTransportationPlan = () => (
    <EmptyContent
      title={translate('Not planned on shipment')}
      bodyText={translate('Shipments associated with this booking will display here when they have been assigned.')}
      hideBorder={true}
      illustration={'none'}
    />
  );

  const TransportationPlanTable = () => (
    <TableContainer data-testid="transportation-plan-table">
      <StyledTable sx={{ border: 'none' }} aria-label={translate('Transportation Plan')}>
        <StyledTableHead>
          <StyledTableRow sx={{ borderTop: 'none' }}>
            <StyledTableCell>{translate('Shipment Number')}</StyledTableCell>

            <StyledTableCell>
              {transportMode === TransportMode.AIR ? translate('HAWB Number') : translate('HBL Number')}
            </StyledTableCell>

            <StyledTableCell>{translate('Planned on Shipment')}</StyledTableCell>
          </StyledTableRow>
        </StyledTableHead>

        <TableBody>
          <StyledTableRow>
            <StyledTableCell>
              <Typography variant="body1">
                {canAccessShipment ? (
                  <Link
                    href={shipmentDetailsBaseUrl + shipmentNumber}
                    aria-label={translate('Click on the link to see more')}
                    sx={{ letterSpacing: '0.5px' }}
                  >
                    {shipmentNumber}
                  </Link>
                ) : (
                  <Typography variant="body1">{shipmentNumber}</Typography>
                )}
              </Typography>
            </StyledTableCell>

            <StyledTableCell>
              <Typography variant="body1">{billOfLadingNumber || '-'}</Typography>
            </StyledTableCell>

            <StyledTableCell>
              <Typography variant="body1">{plannedDate || '-'}</Typography>
            </StyledTableCell>
          </StyledTableRow>
        </TableBody>
      </StyledTable>
    </TableContainer>
  );

  const renderContent = () => {
    if (isLinkedToGfsShipment && !hasShipment) {
      return <InvalidTransportationPlan />;
    }

    if (hasShipment) {
      return <TransportationPlanTable />;
    }

    return <EmptyTransportationPlan />;
  };

  return (
    <Section
      data-testid="transportation-plan"
      className="transportation-plan"
      header={
        <>
          <Typography variant="h3" alignItems="center" display={'flex'}>
            {translate('Transportation Plan')}

            {!canAccessShipment && isLinkedToGfsShipment && hasShipment && (
              <Tooltip
                title={
                  <Box paddingX="5px" paddingY="7px" lineHeight={1.3} fontSize={14}>
                    {translate(
                      'Shipment links are unavailable. Contact your account manager to access this information.'
                    )}
                  </Box>
                }
                placement="right"
              >
                <Icon
                  color="primary"
                  tabIndex={0}
                  aria-hidden="false"
                  className="toolTipIcon"
                  fontSize="medium"
                  sx={{ paddingLeft: 0.2, marginLeft: 1 }}
                >
                  info
                </Icon>
              </Tooltip>
            )}
          </Typography>
        </>
      }
    >
      {renderContent()}
    </Section>
  );
};
